import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Building2, CreditCard, MoreHorizontal, Plus, RotateCcw, User, Users } from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { StatusPill } from '@/components/status-dot';
import { EntityAvatar } from '@/components/entity-avatar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  useClients,
  useForceDeleteClient,
  useRestoreClient,
} from '@/lib/clients';
import { formatStatus } from '@/lib/format';

type StatusFilter = '' | 'active' | 'paused' | 'archived' | 'deleted';
type ClientStatus = 'active' | 'paused' | 'archived' | 'deleted';

function statusTone(status: string): 'success' | 'warning' | 'neutral' | 'danger' {
  if (status === 'active') return 'success';
  if (status === 'paused') return 'warning';
  if (status === 'archived') return 'neutral';
  if (status === 'deleted') return 'danger';
  return 'neutral';
}

const STATUS_FILTERS: StatusFilter[] = ['active', 'paused', 'archived', '', 'deleted'];

export function ClientsIndex() {
  const [searchParams] = useSearchParams();
  const linkedStatus = searchParams.get('status') as StatusFilter | null;
  const [q, setQ] = useState('');
  // Active is what you work with day to day, so it's where the list opens.
  const [status, setStatus] = useState<StatusFilter>(
    linkedStatus !== null && STATUS_FILTERS.includes(linkedStatus) ? linkedStatus : 'active',
  );
  const [page, setPage] = useState(1);
  const viewingDeleted = status === 'deleted';
  const { data: paginated, isLoading } = useClients({
    q: q || undefined,
    status: viewingDeleted ? undefined : status || undefined,
    trashed: viewingDeleted ? 'only' : undefined,
    page,
  });
  const data = paginated?.data;
  const meta = paginated?.meta;
  const restore = useRestoreClient();
  const forceDelete = useForceDeleteClient();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Users}
        title="Clients"
        subtitle={
          viewingDeleted
            ? 'Deleted clients. Restore them or remove them for good.'
            : 'People and organizations you work with.'
        }
        action={
          <Link to="/clients/new">
            <Button>
              <Plus className="size-4" /> New client
            </Button>
          </Link>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by name…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as StatusFilter);
            setPage(1);
          }}
          className="max-w-[180px]"
        >
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="archived">Archived</option>
          <option value="">All clients</option>
          <option value="deleted">Deleted (trash)</option>
        </Select>
      </div>

      {isLoading ? (
        <SkeletonList rows={5} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Users}
          title={viewingDeleted ? 'No deleted clients' : 'No clients yet'}
          description={
            viewingDeleted
              ? 'Deleted clients will show up here so you can restore them within 30 days.'
              : 'Add your first client to start tracking projects, documents, and history.'
          }
          action={
            !viewingDeleted ? (
              <Link to="/clients/new">
                <Button>
                  <Plus className="size-4" /> New client
                </Button>
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-4">
          <DataList>
            {data.map((c) => {
              const location = [c.city, c.state].filter(Boolean).join(', ');
              return (
                <DataListRow
                  key={c.id}
                  onClick={() => !viewingDeleted && navigate(`/clients/${c.id}`)}
                  interactive={!viewingDeleted}
                >
                  <EntityAvatar name={c.name} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        {c.kind === 'individual' ? (
                          <User
                            className="size-3.5 shrink-0 text-muted-foreground"
                            aria-label="Individual"
                          />
                        ) : (
                          <Building2
                            className="size-3.5 shrink-0 text-muted-foreground"
                            aria-label="Company"
                          />
                        )}
                        <span className="truncate font-medium text-foreground">{c.name}</span>
                      </span>
                      {c.legal_name && c.legal_name !== c.name ? (
                        <span className="truncate text-xs text-muted-foreground">{c.legal_name}</span>
                      ) : null}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        {c.contacts_count} {c.contacts_count === 1 ? 'contact' : 'contacts'}
                      </span>
                      {location ? (
                        <>
                          <span aria-hidden>·</span>
                          <span>{location}</span>
                        </>
                      ) : null}
                      {c.stripe_customer_id ? (
                        <>
                          <span aria-hidden>·</span>
                          <span
                            className="inline-flex items-center gap-1 text-primary"
                            title={`Linked to Stripe customer ${c.stripe_customer_id}`}
                          >
                            <CreditCard className="size-3 shrink-0" aria-hidden />
                            Stripe
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <StatusPill tone={statusTone(c.status as ClientStatus)}>
                    {formatStatus(c.status)}
                  </StatusPill>
                  {viewingDeleted ? (
                    <DeletedActions
                      onRestore={async () => {
                        await restore.mutateAsync(c.id);
                        toast.success(`${c.name} restored`);
                      }}
                      onForceDelete={async () => {
                        const ok = await confirm({
                          title: `Permanently delete ${c.name}?`,
                          body: 'This cannot be undone. All contacts, documents, projects, approvables, and timeline history will be removed.',
                          confirmLabel: 'Delete forever',
                          tone: 'danger',
                        });
                        if (ok) {
                          await forceDelete.mutateAsync(c.id);
                          toast.success(`${c.name} permanently deleted`);
                        }
                      }}
                    />
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        onClick={(e) => e.stopPropagation()}
                        className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover/row:opacity-100 aria-expanded:opacity-100"
                        aria-label={`Actions for ${c.name}`}
                      >
                        <MoreHorizontal className="size-4" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => navigate(`/clients/${c.id}`)}>
                          View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/clients/${c.id}/edit`)}>
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/clients/${c.id}?tab=projects`)}>
                          New project
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </DataListRow>
              );
            })}
          </DataList>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function DeletedActions({
  onRestore,
  onForceDelete,
}: {
  onRestore: () => void;
  onForceDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <Button size="sm" variant="secondary" onClick={onRestore}>
        <RotateCcw className="size-3.5" /> Restore
      </Button>
      <Button size="sm" variant="danger" onClick={onForceDelete}>
        <AlertTriangle className="size-3.5" /> Delete forever
      </Button>
    </div>
  );
}
